import { COURSE_STATUSES, type CourseStatus } from '../constants/contentAuthoringEnums';

/** Translation async job status from GET /courses/{id} `translation_jobs`. */
export const TRANSLATION_JOB_STATUSES = {
  QUEUED: 'queued',
  RUNNING: 'running',
  FAILED: 'failed',
} as const;

export type TranslationJobStatus =
  (typeof TRANSLATION_JOB_STATUSES)[keyof typeof TRANSLATION_JOB_STATUSES];

/** Row in the course translations table (completed translation or in-flight job). */
export interface CourseTranslationRow {
  id: string;
  courseName: string;
  language: string;
  languageLabel: string;
  status: CourseStatus | TranslationJobStatus;
  /**
   * The backend translation status verbatim, when the row came from one (CEE-740).
   *
   * `status` above is the GRID's vocabulary, and it cannot express this one: both
   * `ready_for_approval` and `approved` arrive as `ready`. The row-action rules are written
   * against the backend's own values, so they read this. Optional because a job row has no
   * translation status, and the source-language row's status belongs to the other vocabulary
   * entirely — both of those are decided by `isJob` / `isSourceLanguage` before it is consulted.
   */
  rawStatus?: string;
  isLocked: boolean;
  isStale: boolean;
  /**
   * Archived (soft-deleted) — derived by the API from `deleted_at`, never a status value
   * (CEE-1066). Archived languages stay in the grid, labelled Archived and offering no
   * actions, which is what the archive dialog promises ("It will remain in the list").
   * Absent on a job row: a job has nothing to archive.
   */
  archived?: boolean;
  translatedAt: string | null;
  reviewedBy: string | null;
  reviewedAt: string | null;
  updatedAt: string;
  /** True when the row represents an async job rather than a persisted translation. */
  isJob: boolean;
  /** True for the parent course's source language row (e.g. English), not a translation child. */
  isSourceLanguage?: boolean;
  jobId?: string;
  /** Translation course id on a job row — used for discard when the job failed before a row surfaced. */
  translationCourseId?: string | null;
  errorMessage?: string | null;
}

export function getCourseTranslationStatusKey(
  status: CourseTranslationRow['status'],
  isLocked: boolean,
  reviewedAt: string | null,
  isSourceLanguage = false,
  archived = false,
): string {
  // Archived wins over every other branch, including `isLocked` and `isSourceLanguage`
  // (CEE-1066). It is not a status — the row still carries ready_for_approval or approved
  // underneath — but it is the only thing about the row that is still actionable
  // information, and showing "Approved" on a language somebody archived misreports it.
  if (archived) {
    return 'page.authoring_courses.translations.status.archived';
  }

  if (isSourceLanguage) {
    return 'page.authoring_courses.translations.status.source';
  }

  if (status === COURSE_STATUSES.PUBLISHED) {
    return 'page.authoring_courses.translations.status.published';
  }

  if (isLocked) {
    return 'page.authoring_courses.translations.status.locked';
  }

  switch (status) {
    case COURSE_STATUSES.SUBMITTED:
      return 'page.authoring_courses.status.submitted';
    case COURSE_STATUSES.READY:
      return reviewedAt
        ? 'page.authoring_courses.translations.status.approved'
        : 'page.authoring_courses.translations.status.ready';
    case COURSE_STATUSES.DRAFT:
      return 'page.authoring_courses.translations.status.draft';
    case TRANSLATION_JOB_STATUSES.QUEUED:
    case TRANSLATION_JOB_STATUSES.RUNNING:
      return 'page.authoring_courses.translations.status.translating';
    case TRANSLATION_JOB_STATUSES.FAILED:
      return 'page.authoring_courses.translations.status.failed';
    default:
      return 'page.authoring_courses.translations.status.draft';
  }
}
